import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { logAudit } from '../lib/auditLog';
import { useGenreTaxonomy } from '../../hooks/useGenreTaxonomy';
import GenreSubgenrePicker from '../../components/genre/GenreSubgenrePicker';

// An admin's picks are credited 'author', the same rule the tag editor follows.
const ADMIN_SOURCE = 'author';
const SOURCE_NOTE = { author: 'author', reader: 'reader', api: 'api' };

function PickChip({ on, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{ fontSize: 'var(--font-size-xs)', padding: '4px 11px', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontFamily: 'var(--font-body)',
      border: on ? '1px solid #93c5fd' : '1px dashed #93c5fd', background: on ? '#dbeafe' : 'transparent', color: '#1e40af' }}>
      {children}
    </button>
  );
}

function SourceBadge({ source }) {
  return (
    <span title="Who added it. An admin can remove anyone's, and those are different acts."
      style={{ fontSize: 'var(--font-size-micro)', fontWeight: 700, padding: '2px 7px', borderRadius: 'var(--radius-10)', background: 'var(--brand-cream)', color: 'var(--neutral-600)', border: '1px solid var(--brand-sand)' }}>
      added by {SOURCE_NOTE[source] || 'unknown'}
    </span>
  );
}

// Adding or removing a genre can change the book's status without this screen doing it: a database
// trigger re-runs the publish gate whenever a book's genres change (20260926000000_pr5_stop_book_tags_genre_write.sql).
// So every save and every removal reads the status back and says if it moved.
const STATUS_WORDS = { live: 'live', pending: 'pending, which takes it off the site', flagged: 'flagged', removed: 'removed' };

export default function GenresPanel({ book, onClose, onChanged }) {
  const tax = useGenreTaxonomy();
  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [note, setNote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [genreIds, setGenreIds] = useState([]);
  const [subgenreIds, setSubgenreIds] = useState([]);
  const [subgenreNames, setSubgenreNames] = useState({});
  const statusRef = useRef(book.status);

  const reload = useCallback(async () => {
    const [g, s] = await Promise.all([
      supabase.from('book_genres').select('id, source, genre:genres ( id, name )').eq('book_id', book.id),
      supabase.from('book_subgenres').select('id, source, subgenre:subgenres ( id, name, genre_id, status )').eq('book_id', book.id),
    ]);
    const err = g.error || s.error;
    if (err) { setLoadError(err.message); return; }
    setLoadError(null);
    setRows({ genres: g.data || [], subgenres: s.data || [] });
  }, [book.id]);

  useEffect(() => { reload(); }, [reload]);

  // Reads the status back after a change. Returns { before, after } when it moved, { unknown: true } when
  // the read failed, or null when it did not move.
  async function statusMoved() {
    const { data, error } = await supabase.from('books').select('status').eq('id', book.id).maybeSingle();
    if (error || !data) return { unknown: true };
    const before = statusRef.current;
    statusRef.current = data.status;
    return data.status === before ? null : { before, after: data.status };
  }

  async function remove(table, row, kind, name) {
    setNote(null);
    // The last genre on a live book: the publish gate then fails and the book leaves the site.
    if (table === 'book_genres' && statusRef.current === 'live' && rows.genres.length === 1 &&
        !confirm(`"${name}" is the only genre on "${book.title}". Removing it takes the book off the site until it has a genre again. Remove it?`)) return;
    const { data, error } = await supabase.from(table).delete().eq('id', row.id).select('id');
    if (error) { setNote({ bad: true, text: `${name} NOT removed, it is still on the book: ${error.message}` }); return; }
    if (!data?.length) {
      setNote({ bad: true, text: `${name} NOT removed. The database refused it without saying why, so it is still on the book.` });
      await reload();
      return;
    }
    const moved = await statusMoved();
    const movedText = moved?.after ? ` The book is now ${STATUS_WORDS[moved.after] || moved.after} (it was ${moved.before}).` : '';
    await logAudit('delete', 'book', `Removed ${kind} "${name}" (added by ${row.source || 'unknown'}) from "${book.title}"${moved?.after ? `, which moved it from ${moved.before} to ${moved.after}` : ''}`, book.id);
    setNote({
      bad: !!moved?.after || !!moved?.unknown,
      text: `Removed ${kind} "${name}".${movedText}${moved?.unknown ? " Couldn't read the book's status back, so check whether it is still live." : ''}`,
    });
    await reload();
    if (moved?.after) onChanged?.(`Removed ${kind} "${name}" from "${book.title}". It is now ${STATUS_WORDS[moved.after] || moved.after} (it was ${moved.before}).`);
  }

  function removeGenre(row) {
    const name = row.genre?.name || 'genre';
    if (rows.subgenres.some(s => s.subgenre?.genre_id === row.genre?.id)) {
      setNote({ bad: true, text: `Remove the subgenres under ${name} first. A subgenre needs its genre.` });
      return;
    }
    remove('book_genres', row, 'genre', name);
  }

  async function savePicks() {
    setSaving(true); setNote(null);
    const parentOf = (sid) => subgenreNames[sid]?.genreId ?? tax.subgenresById.get(sid)?.genre_id;
    const wantGenres = [...new Set([...genreIds, ...subgenreIds.map(parentOf).filter(Boolean)])];
    const added = [];
    const failed = [];

    if (wantGenres.length) {
      const { data, error } = await supabase.from('book_genres')
        .upsert(wantGenres.map(genre_id => ({ book_id: book.id, genre_id, source: ADMIN_SOURCE })), { onConflict: 'book_id,genre_id', ignoreDuplicates: true })
        .select('id, genre_id');
      if (error) failed.push(`genres: ${error.message}`);
      else (data || []).forEach(r => added.push(`genre "${tax.genresById.get(r.genre_id)?.name || r.genre_id}"`));
    }
    if (subgenreIds.length && !failed.length) {
      const { data, error } = await supabase.from('book_subgenres')
        .upsert(subgenreIds.map(subgenre_id => ({ book_id: book.id, subgenre_id, source: ADMIN_SOURCE })), { onConflict: 'book_id,subgenre_id', ignoreDuplicates: true })
        .select('id, subgenre_id');
      if (error) failed.push(`subgenres: ${error.message}`);
      else (data || []).forEach(r => added.push(`subgenre "${tax.subgenresById.get(r.subgenre_id)?.name || subgenreNames[r.subgenre_id]?.name || r.subgenre_id}"`));
    }

    const moved = added.length ? await statusMoved() : null;
    const movedAudit = moved?.after ? `, which moved it from ${moved.before} to ${moved.after}` : '';
    if (added.length) await logAudit('create', 'book', `Added ${added.join(', ')} to "${book.title}" (source ${ADMIN_SOURCE})${movedAudit}`, book.id);
    setGenreIds([]); setSubgenreIds([]); setSubgenreNames({});
    await reload();
    setSaving(false);
    const movedText = moved?.after === 'live' && moved.before !== 'live'
      ? ' It meets the publish gate now, so it\'s live.'
      : moved?.after ? ` The book is now ${STATUS_WORDS[moved.after] || moved.after} (it was ${moved.before}).` : '';
    const unknownText = moved?.unknown ? " Couldn't read the book's status back, so check the lists." : '';
    if (failed.length) {
      setNote({ bad: true, text: `Not everything was saved (${failed.join('; ')}). ${added.length ? `Saved: ${added.join(', ')}.` : 'Nothing was added.'}${movedText}${unknownText}` });
    } else {
      setNote({ bad: !!moved?.unknown, text: added.length ? `Added ${added.join(', ')}.${movedText}${unknownText}` : 'Already on the book, nothing new was added.' });
    }
    if (moved?.after) onChanged?.(`Added ${added.join(', ')} to "${book.title}".${movedText}`);
  }

  const nothingPicked = genreIds.length === 0 && subgenreIds.length === 0;
  const label = { fontSize: 'var(--font-size-xxs)', fontWeight: 700, color: 'var(--neutral-500)', textTransform: 'uppercase', letterSpacing: 0.5, margin: '10px 0 5px' };
  const rowStyle = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '5px 0', fontSize: 'var(--font-size-sm)', color: 'var(--neutral-800)' };
  const removeBtn = { background: 'none', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', padding: '2px 9px', fontSize: 'var(--font-size-xs)', color: '#991b1b', cursor: 'pointer', fontFamily: 'var(--font-body)' };

  return (
    <div data-genres-panel style={{ marginTop: 'var(--space-md)', padding: '14px 16px', background: 'var(--brand-paper)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-10)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--neutral-800)' }}>Genres and subgenres</span>
        <button type="button" onClick={onClose} aria-label="Close genres" style={{ background: 'none', border: 'none', fontSize: 'var(--font-size-md)', cursor: 'pointer', color: 'var(--neutral-400)' }}>✕</button>
      </div>

      {loadError && <div role="alert" style={{ marginTop: 8, fontSize: 'var(--font-size-sm)', color: '#991b1b' }}>Couldn&apos;t read this book&apos;s genres: {loadError}. That is not the same as it having none.</div>}
      {!rows && !loadError && <div style={{ marginTop: 8, fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)' }}>Loading…</div>}

      {rows && (
        <>
          <div style={label}>On this book</div>
          {rows.genres.length === 0 && rows.subgenres.length === 0 && <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-500)', fontStyle: 'italic' }}>No genre yet.</div>}
          {rows.genres.map(r => (
            <div key={`g${r.id}`} style={rowStyle} data-genre-row>
              <strong>{r.genre?.name || 'Genre'}</strong> <SourceBadge source={r.source} />
              <button type="button" style={removeBtn} onClick={() => removeGenre(r)}>Remove</button>
            </div>
          ))}
          {rows.subgenres.map(r => (
            <div key={`s${r.id}`} style={{ ...rowStyle, paddingLeft: 14 }} data-subgenre-row>
              {r.subgenre?.name || 'Subgenre'}
              <span style={{ color: 'var(--neutral-400)', fontSize: 'var(--font-size-xs)' }}>under {tax.genresById.get(r.subgenre?.genre_id)?.name || 'a genre'}{r.subgenre?.status === 'pending' ? ', not yet reviewed' : ''}</span>
              <SourceBadge source={r.source} />
              <button type="button" style={removeBtn} onClick={() => remove('book_subgenres', r, 'subgenre', r.subgenre?.name || 'subgenre')}>Remove</button>
            </div>
          ))}
        </>
      )}

      {note && <div role={note.bad ? 'alert' : 'status'} data-genres-note style={{ marginTop: 8, fontSize: 'var(--font-size-sm)', color: note.bad ? '#991b1b' : '#065f46' }}>{note.text}</div>}

      <div style={label}>Add</div>
      <GenreSubgenrePicker
        Chip={PickChip}
        genres={tax.genres}
        canonSubgenresForGenre={tax.canonSubgenresForGenre}
        matchIndex={tax.matchIndex}
        upsertPendingSubgenre={tax.upsertPendingSubgenre}
        selectedGenreIds={genreIds}
        setSelectedGenreIds={setGenreIds}
        selectedSubgenreIds={subgenreIds}
        setSelectedSubgenreIds={setSubgenreIds}
        subgenreNamesById={subgenreNames}
        setSubgenreNamesById={setSubgenreNames}
        loading={tax.loading}
        error={tax.error}
      />
      <div style={{ marginTop: 10 }}>
        <button type="button" onClick={savePicks} disabled={saving || nothingPicked}
          style={{ padding: '6px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--neutral-800)', background: 'var(--neutral-800)', color: 'var(--brand-white)', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: saving || nothingPicked ? 'not-allowed' : 'pointer', opacity: saving || nothingPicked ? 0.5 : 1, fontFamily: 'var(--font-body)' }}>
          {saving ? 'Saving…' : 'Save picks'}
        </button>
      </div>
    </div>
  );
}
